import { ClassificationsProviderV2 } from '@/providers/classification/classifications-v2.provider';
import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiSectionActionsDirective } from '@/ui/section/section-actions/ui-section-actions';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { PluralizePipe } from '@/utils/i18n/pluralize.pipe';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, inject, type OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, type Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { TrackClickDirective } from '../../../directives/track-click/track-click.directive';
import type { BuildingId } from '../../../interfaces/Building.interface';
import { buildingIdToClassificationNodeId, type ClassificationNodeId } from '../../../interfaces/Classification.interface';
import { LocalNumberPipe } from '../../../pipes/localnumber.pipe';
import { BuildingsProvider } from '../../../providers/buildings/buildings.provider';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import { ListOfContactsComponent } from '../../list-of-contacts/list-of-contacts';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { NodePictureComponent } from '../../node-picture/node-picture';
import { AbstractWidgetClass } from '../AbstractWidget.class';

@Component({
    styleUrls: ['./widget-node-info.scss'],
    selector: 'widget-node-info',
    templateUrl: 'widget-node-info.html',
    imports: [
        NodePictureComponent,
        UiSectionComponent,
        UiSectionActionsDirective,
        UiButtonComponent,
        TrackClickDirective,
        UiSectionContentComponent,
        LoadingAndErrorComponent,
        ListOfContactsComponent,
        AsyncPipe,
        TranslatePipe,
        PluralizePipe
    ]
})
export class WidgetNodeInfoComponent extends AbstractWidgetClass<null, void> implements OnInit {
    private _buildingsProvider = inject(BuildingsProvider);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _classificationProvider = inject(ClassificationsProviderV2);
    private _router = inject(Router);

    size = WidgetSize.FULL;

    nodeLength$!: Observable<number>;
    buildingId$!: Observable<BuildingId>;
    loading: boolean = false;

    private _localNumberPipe: LocalNumberPipe;

    classificationNodeDetails$!: Observable<{
        classificationNodeDetails: { code: string; label: string }[];
        meterPlanDetails: { code: string; label: string }[];
    }>;

    constructor() {
        super();
        this._localNumberPipe = new LocalNumberPipe();
    }

    ngOnInit() {
        this.nodeLength$ = this._dashboardingProvider.getMeaningfulBuildingIds$().pipe(map(buildingIds => buildingIds.length));
        this.buildingId$ = this._dashboardingProvider.getMeaningfulBuildingIds$().pipe(
            filter(buildingIds => isNotNullish(buildingIds) && Array.isArray(buildingIds) && buildingIds.length === 1),
            map(buildingIds => buildingIds[0])
        );

        this.classificationNodeDetails$ = this._dashboardingProvider.getIsMonoBuilding$().pipe(
            switchMap(isMonoBuilding => {
                if (isMonoBuilding) {
                    return this._dashboardingProvider.getMeaningfulBuildingId$().pipe(
                        tap(() => (this.loading = true)),
                        switchMap(buildingId => this.getMonoBuildingData$(buildingId)),
                        tap(() => (this.loading = false))
                    );
                } else {
                    return this._dashboardingProvider.getSelectedNodeId$().pipe(
                        tap(() => (this.loading = true)),
                        switchMap(nodeId => this.getMultiBuildingData$(nodeId)),
                        tap(() => (this.loading = false))
                    );
                }
            }),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }

    async goToBuildingEdition() {
        const buildingId = await firstValueFrom(this.buildingId$);
        this._router.navigate(['/', 'buildings', buildingId]);
    }

    getMonoBuildingData$(buildingId: BuildingId): Observable<{
        classificationNodeDetails: { code: string; label: string }[];
        meterPlanDetails: { code: string; label: string }[];
    }> {
        return combineLatest([
            this._classificationProvider.getClassificationNodePatrimonialData$(buildingIdToClassificationNodeId(buildingId)),
            this._buildingsProvider.getBuilding$(buildingId),
            this._buildingsProvider.getBuildingDetails$(buildingId)
        ]).pipe(
            map(([patrimonialData, building, buildingDetails]) => {
                return {
                    classificationNodeDetails: [
                        {
                            code: 'ENTITIES.BUILDING.TYPE',
                            label: this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.BUILDING, building.typeCode)
                        },
                        {
                            code: 'ENTITIES.BUILDING.SURFACE',
                            label: this._localNumberPipe.transform(patrimonialData.usefulSurface, 0)
                        },
                        {
                            code: 'ENTITIES.BUILDING.SHON_SURFACE',
                            label: this._localNumberPipe.transform(patrimonialData.shonSurface, 0)
                        },
                        {
                            code: 'ENTITIES.BUILDING.VOLUME',
                            label: this._localNumberPipe.transform(patrimonialData.volume, 0)
                        },
                        {
                            code: 'ENTITIES.BUILDING.FLOORS',
                            label: this._localNumberPipe.transform(buildingDetails.floorCount)
                        },
                        {
                            code: 'ENTITIES.BUILDING.PEOPLE_COUNT',
                            label: this._localNumberPipe.transform(patrimonialData.peopleCount)
                        },
                        {
                            code: 'ENTITIES.BUILDING.CONSTRUCTION_YEAR',
                            label: buildingDetails.builtYear ? String(buildingDetails.builtYear) : '--'
                        },
                        {
                            code: 'ENTITIES.BUILDING.RENOVATION_YEAR',
                            label: buildingDetails.lastRenovationYear ? String(buildingDetails.lastRenovationYear) : '--'
                        }
                    ],
                    meterPlanDetails: [
                        {
                            code: 'ENTITIES.METER.DELIVERY_POINT_NUMBER',
                            label: this._localNumberPipe.transform(patrimonialData.deliveryPointCount)
                        },
                        {
                            code: 'ENTITIES.METER.SUB_METER_NUMBER',
                            label: this._localNumberPipe.transform(patrimonialData.meterCount)
                        },
                        {
                            code: 'ENTITIES.METER.DEVICES_NUMBER',
                            label: this._localNumberPipe.transform(patrimonialData.sensorCount)
                        }
                    ]
                };
            })
        );
    }

    getMultiBuildingData$(
        classificationNodeId: ClassificationNodeId
    ): Observable<{ classificationNodeDetails: { code: string; label: string }[]; meterPlanDetails: { code: string; label: string }[] }> {
        return this._classificationProvider.getClassificationNodePatrimonialData$(classificationNodeId).pipe(
            map(patrimonialData => {
                return {
                    classificationNodeDetails: [
                        {
                            code: 'ENTITIES.NODE.NUMBER',
                            label: this._localNumberPipe.transform(patrimonialData.buildingCount)
                        },
                        {
                            code: 'ENTITIES.BUILDING.SURFACE',
                            label: this._localNumberPipe.transform(patrimonialData.usefulSurface, 0)
                        },
                        {
                            code: 'ENTITIES.BUILDING.SHON_SURFACE',
                            label: this._localNumberPipe.transform(patrimonialData.shonSurface, 0)
                        },
                        {
                            code: 'ENTITIES.BUILDING.VOLUME',
                            label: this._localNumberPipe.transform(patrimonialData.volume, 0)
                        },
                        {
                            code: 'ENTITIES.BUILDING.PEOPLE_COUNT',
                            label: this._localNumberPipe.transform(patrimonialData.peopleCount)
                        }
                    ],
                    meterPlanDetails: [
                        {
                            code: 'ENTITIES.METER.DELIVERY_POINT_NUMBER',
                            label: this._localNumberPipe.transform(patrimonialData.deliveryPointCount)
                        },
                        {
                            code: 'ENTITIES.METER.SUB_METER_NUMBER',
                            label: this._localNumberPipe.transform(patrimonialData.meterCount)
                        },
                        {
                            code: 'ENTITIES.METER.DEVICES_NUMBER',
                            label: this._localNumberPipe.transform(patrimonialData.sensorCount)
                        }
                    ]
                };
            })
        );
    }
}
